// Semantic Analyzer types and Zod schemas (ARCHITECTURE.md §11.4–11.6).
//
// The Semantic Analyzer fills gaps the deterministic pipeline cannot derive from
// syntax alone, using LLM inference on targeted source excerpts. It produces
// `candidate` GraphEdges that integrate into the same graph as deterministic
// edges.
//
// As elsewhere in the codebase, the inferred Zod types are the compile-time
// source of truth; the schemas provide runtime validation at trust boundaries
// (LLM responses, plugin input, CLI args).

import { z } from "zod";

import { EdgeKindSchema, GraphEdgeSchema } from "../types.js";
import { EvidenceTypeSchema } from "../evidence/types.js";
import { SymbolInfoSchema } from "../analyzer/types.js";

// ---------------------------------------------------------------------------
// GapReason
// ---------------------------------------------------------------------------

export const GAP_REASONS = [
  "contract_related_no_edge", // contract graph says related, AST says not
  "same_artifact_no_edge", // same artifact, likely related, no edge
  "naming_convention_match", // file names suggest a relationship
  "unresolved_dynamic_import", // dynamic import with a non-literal argument
] as const;

export const GapReasonSchema = z.enum(GAP_REASONS);

export type GapReason = z.infer<typeof GapReasonSchema>;

// ---------------------------------------------------------------------------
// GapCandidate
// ---------------------------------------------------------------------------

export const GapCandidateSchema = z.object({
  fileA: z.string(),
  fileB: z.string(),
  gapReason: GapReasonSchema,
  contractRelation: z
    .object({
      fromArtifact: z.string(),
      toArtifact: z.string(),
      // e.g. "source_artifacts", "derived_artifacts"
      relation: z.string(),
    })
    .optional(),
});

export type GapCandidate = z.infer<typeof GapCandidateSchema>;

// ---------------------------------------------------------------------------
// SourceSnippet
// ---------------------------------------------------------------------------

export const SNIPPET_CONTEXTS = [
  "signature", // function/class/type signature
  "body", // function body (for short functions)
  "usage", // where a symbol is used
  "surrounding", // context around an unresolved reference
] as const;

export const SnippetContextSchema = z.enum(SNIPPET_CONTEXTS);

export type SnippetContext = z.infer<typeof SnippetContextSchema>;

export const SourceSnippetSchema = z.object({
  startLine: z.number().int().positive(),
  endLine: z.number().int().positive(),
  // raw source code (a few lines)
  content: z.string(),
  context: SnippetContextSchema,
});

export type SourceSnippet = z.infer<typeof SourceSnippetSchema>;

// ---------------------------------------------------------------------------
// FileContext
// ---------------------------------------------------------------------------

export const FileContextSchema = z.object({
  filePath: z.string(),
  symbols: z.array(SymbolInfoSchema), // from store (Layer 2)
  existingEdges: z.array(GraphEdgeSchema), // from store (Layer 2)
  snippets: z.array(SourceSnippetSchema), // from file system (Layer 1)
});

export type FileContext = z.infer<typeof FileContextSchema>;

// ---------------------------------------------------------------------------
// GapDescription
// ---------------------------------------------------------------------------

export const GapDescriptionSchema = z.object({
  reason: GapReasonSchema,
  // human-readable summary of the contract relation, when applicable
  contractRelation: z.string().optional(),
  confidence: z.number().min(0).max(1),
});

export type GapDescription = z.infer<typeof GapDescriptionSchema>;

// ---------------------------------------------------------------------------
// SourceContext
// ---------------------------------------------------------------------------

export const SourceContextSchema = z.object({
  file: FileContextSchema,
  relatedFiles: z.array(FileContextSchema),
  gap: GapDescriptionSchema,
});

export type SourceContext = z.infer<typeof SourceContextSchema>;

// ---------------------------------------------------------------------------
// LlmEvidencePoint
// ---------------------------------------------------------------------------

export const LlmEvidencePointSchema = z.object({
  type: EvidenceTypeSchema,
  detail: z.string(),
  // 1-based source line the evidence was observed on, when applicable
  line: z.number().int().positive().optional(),
});

export type LlmEvidencePoint = z.infer<typeof LlmEvidencePointSchema>;

// ---------------------------------------------------------------------------
// CandidateProposal
// ---------------------------------------------------------------------------

export const CandidateProposalSchema = z.object({
  from: z.string(),
  to: z.string(),
  edgeKind: EdgeKindSchema,
  confidence: z.number().min(0).max(1),
  reasoning: z.string(),
  evidencePoints: z.array(LlmEvidencePointSchema),
});

export type CandidateProposal = z.infer<typeof CandidateProposalSchema>;

/** Array form used when parsing a full LLM response. */
export const CandidateProposalArraySchema = z.array(CandidateProposalSchema);

// ---------------------------------------------------------------------------
// LlmAdapter & options
// ---------------------------------------------------------------------------

// LlmAdapter carries a behavioural method, so it is a TypeScript interface
// rather than a Zod schema — functions are not meaningfully validatable at
// runtime. Semantic analysis is always opt-in; the user supplies the adapter.
export interface LlmAdapter {
  analyze(prompt: string): Promise<string>;
}

export interface SemanticAnalyzerOptions {
  /** Cap on candidate edges emitted per run (default: 100). */
  maxCandidatesPerRun?: number;
  /** Drop gaps below this confidence before LLM invocation (default: 0.5). */
  minGapConfidence?: number;
  llmAdapter: LlmAdapter;
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

/** Default cap on candidate edges per run (ARCHITECTURE.md §11.10). */
export const DEFAULT_MAX_CANDIDATES_PER_RUN = 100;

/** Default minimum gap confidence (ARCHITECTURE.md §11.10). */
export const DEFAULT_MIN_GAP_CONFIDENCE = 0.5;

/** Default context window (lines before/after a symbol) for snippets. */
export const DEFAULT_SNIPPET_CONTEXT_LINES = 5;

/**
 * Heuristic confidence assigned to each gap reason. These express how strongly
 * the deterministic signal suggests a real relationship worth asking the LLM
 * about; they are NOT the confidence of the resulting candidate edge (that
 * comes from the LLM's self-assessment).
 */
export const GAP_REASON_CONFIDENCE: Record<GapReason, number> = {
  contract_related_no_edge: 0.7,
  same_artifact_no_edge: 0.6,
  unresolved_dynamic_import: 0.55,
  naming_convention_match: 0.5,
};
